import axios, { AxiosResponse } from 'axios';
import {
  JWT_CREATE_API_URL,
  JWT_REFRESH_API_URL,
  JWT_VALIDATE_API_URL,
  USER_CREATION_API_URL,
  USER_ME_INFO_API_URL,
  ACTIVATE_USER_API_URL,
  RESEND_ACTIVATION_EMAIL_API_URL,
  GITHUB_OAUTH_API_URL,
  USER_CHANGE_PASSWORD_API_URL,
  REQUEST_USER_RESET_PASSWORD_API_URL,
  USER_RESET_PASSWORD_API_URL,
  USER_COMMUNICATION_SETTINGS_API_URL,
  CONTACT_MESSAGE_API_URL
} from '../config/constants';

// JWT creation request response
export interface JWTCreateResponse {
  access: string
  refresh: string
};

// JWT creation API request
export const jwtCreate = async (usernameOrEmail: string, password: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    JWT_CREATE_API_URL,
    {
      username: usernameOrEmail,
      password
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

// JWT refresh request response
export interface JWTRefreshResponse {
  access: string
};

// JWT refresh API request
export const jwtRefresh = async (refreshToken: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    JWT_REFRESH_API_URL,
    {
      refresh: refreshToken
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

// JWT refresh API request
export const jwtValidate = async (token: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    JWT_VALIDATE_API_URL,
    {
      token
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

// user creation request
export const userCreate = async (username: string, email: string, password: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    USER_CREATION_API_URL,
    {
      username,
      email,
      password
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

export interface GitHubOAuthUserInfoResponse {
  id: string
  github_id: string
  username: string
  email: string
};

export interface UserInfoResponse {
  uuid: string
  username?: string
  display_username: string
  email?: string
  date_joined: string
  github_oauth_user: GitHubOAuthUserInfoResponse | null
};

// user me info request
export const userMeInfo = async (accessToken: string | null): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.get(
    USER_ME_INFO_API_URL,
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    }
  );

  return response;
};

// user activation API request
export const activateUser = async (token: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    ACTIVATE_USER_API_URL,
    {
      token
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

// resend user activation email API request
export const resendActivationEmail = async (email: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    RESEND_ACTIVATION_EMAIL_API_URL,
    {
      email
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

// github authentication API request
export const authenticateGitHub = async (code: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    GITHUB_OAUTH_API_URL,
    {
      code
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
}

// user change password API request
export const userChangePassword = async (accessToken: string | null, currentPassword: string, newPassword: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.patch(
    USER_CHANGE_PASSWORD_API_URL,
    {
      current_password: currentPassword,
      new_password: newPassword,
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    }
  );

  return response;
};

// request user reset password API request
export const requestUserResetPassword = async (email: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    REQUEST_USER_RESET_PASSWORD_API_URL,
    {
      email
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

// user reset password API request
export const userResetPassword = async (token: string, newPassword: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    USER_RESET_PASSWORD_API_URL,
    {
      token,
      new_password: newPassword
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

export interface UserCommunicationSettingsResponse {
  opt_out_of_all: boolean
  monthly_updates: boolean
};

// user get communication settings request
export const getUserCommunicationSettings = async (accessToken: string | null): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.get(
    USER_COMMUNICATION_SETTINGS_API_URL,
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    }
  );

  return response;
};

// user update communication settings request
export const updateUserCommunicationSettings = async (accessToken: string | null, settings: UserCommunicationSettingsResponse): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.put(
    USER_COMMUNICATION_SETTINGS_API_URL,
    settings,
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    }
  );

  return response;
};

// contact message request
export const contactMessage = async (email: string, subject: string, message: string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    CONTACT_MESSAGE_API_URL,
    {
      email,
      subject,
      message
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};
